// Custom hook for managing cloud account form state
import { useState, useCallback, useEffect } from 'react';

export const useCloudAccountForm = (cloudType, isEditMode, initialData) => {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        password: '',
        confirm_password: '',
        url: '',
        tenant_id: '',
        vsphere_version: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialData) {
            const initialFormData = {
                name: initialData.name || '',
                username: initialData.username || '',
                password: initialData.password || '',
                confirm_password: '',
                url: initialData.url || '',
                vsphere_version: initialData.vsphere_version || '',
                ...initialData,
            };

            // Only include tenant_id if it has a value
            if (initialData.tenant_id) {
                initialFormData.tenant_id = initialData.tenant_id;
            }

            setFormData(initialFormData);
        }
    }, [initialData]);

    const handleFieldChange = useCallback(
        (fieldName) => (e) => {
            const value = e.target ? e.target.value : e;
            setFormData((prev) => ({
                ...prev,
                [fieldName]: value,
            }));

            // Clear error for this field when user starts typing
            setErrors((prev) => {
                if (prev[fieldName]) {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                }
                return prev;
            });
        },
        [],
    );

    /**
     * Set multiple fields at once
     */
    const setFields = useCallback((fields) => {
        setFormData((prev) => ({
            ...prev,
            ...fields,
        }));
    }, []);

    /**
     * Reset form to initial state
     */
    const resetForm = useCallback(() => {
        setFormData({
            name: '',
            username: '',
            password: '',
            confirm_password: '',
            url: '',
            tenant_id: '',
            vsphere_version: '',
        });
        setErrors({});
    }, []);

    /**
     * Set form data from loaded cloud account
     */
    const loadFormData = useCallback((data) => {
        const formDataToSet = {
            name: data.name || '',
            username: data.username || '',
            password: '',
            confirm_password: '',
            url: data.url || '',
            vsphere_version: data.vsphere_version || '',
            ...data,
        };

        if (data.tenant_id) {
            formDataToSet.tenant_id = data.tenant_id;
        }

        setFormData(formDataToSet);
    }, []);

    /**
     * Apply default URL when cloud type changes
     */
    const applyDefaultUrl = useCallback((defaultUrl) => {
        if (defaultUrl) {
            setFormData((prev) => ({
                ...prev,
                url: defaultUrl,
                username: '',
                password: '',
                confirm_password: '',
                tenant_id: '',
            }));
        }
    }, []);

    return {
        formData,
        errors,
        setErrors,
        handleFieldChange,
        setFields,
        resetForm,
        loadFormData,
        applyDefaultUrl,
    };
};
